import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from 'recharts'
import { AppDispatch, RootState } from 'store'
import { KpiCard } from '../components/KpiCard'
import { ChartCard } from '../components/ChartCard'
import { AnalyticsTable, RateCellRenderer, RedValueRenderer } from '../components/AnalyticsTable'
import { SubTabBar } from '../components/SubTabBar'
import {
  KpiRow,
  StatusBarWrapper,
  StatusBar,
  StatusSegment,
  StatusLegend,
  LegendItem,
  LegendDot,
  ChartCardWrapper,
  ChartCardTitle,
} from '../HrAnalytics.styled'
import {
  clearHrAnalytics,
  getAttendanceByDepartment,
  getAttendanceByRole,
  getAttendanceRateTrend,
  getAttendanceStatusDistribution,
  getAttendanceSummary,
} from 'features/hrAnalytics/hrAnalytics.slice'

const KpiIcons: Record<string, React.ReactNode> = {
  person: <svg viewBox='0 0 24 24'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' /></svg>,
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  warning: <svg viewBox='0 0 24 24'><path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' /></svg>,
  chart: <svg viewBox='0 0 24 24'><path d='M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z' /></svg>,
}

const DEPT_COLUMNS = [
  { key: 'name', label: 'Department', sortable: true, width: '20%' },
  { key: 'expected', label: 'Expected', sortable: true, width: '20%' },
  { key: 'present', label: 'Present', sortable: true, width: '20%' },
  {
    key: 'absent',
    label: 'Absent',
    sortable: true,
    width: '15%',
    renderCell: (val: number) => <RedValueRenderer value={val} />,
  },
  {
    key: 'rate',
    label: 'Rate',
    sortable: true,
    width: '25%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const STATUS_META: Record<number, { label: string; color: string }> = {
  1: { label: 'Present', color: '#00B04C' },
  2: { label: 'Absent', color: '#FF4F46' },
  3: { label: 'Leave', color: '#ff9900' },
  4: { label: 'Excused', color: '#30B6D1' },
  5: { label: 'Holiday Out', color: '#9C27B0' },
  6: { label: 'Sick Leave', color: '#00BCD4' },
  7: { label: 'Off Day', color: '#BDBDBD' },
  8: { label: 'Training', color: '#8BC34A' },
  9: { label: 'Vacation', color: '#FFC107' },
  10: { label: 'Maternity', color: '#E91E63' },
  11: { label: 'Holiday In', color: '#d23f70' },
}

const UNKNOWN_STATUS = { label: 'Unknown', color: '#757575' }

const PERIOD_MAP: Record<string, 1 | 2 | 3> = {
  Day: 1,
  Week: 2,
  Month: 3,
}

const toPercent = (value: number | null) => {
  if (value == null) return 0
  return value > 1 ? Math.round(value * 10) / 10 : Math.round(value * 1000) / 10
}

const toPercentLabel = (value: number | null) => {
  if (value == null) return '—'
  const pct = value > 1 ? value : value * 100
  return `${pct.toFixed(1)}%`
}

const formatChange = (value: number | null) => {
  if (value == null) return undefined
  const pct = Math.abs(value) > 1 ? value : value * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}

const formatShortDate = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}



export const AttendanceTab = ({ startDate, endDate }: ITab) => {
  const dispatch = useDispatch<AppDispatch>()
  const [period, setPeriod] = useState('Day')
  const [tableTab, setTableTab] = useState('department')

  const {
    attendanceSummary,
    attendanceByDepartment,
    attendanceByRole,
    attendanceRateTrend,
    attendanceStatusDistribution,
  } = useSelector((state: RootState) => state.hrAnalytics)

  useEffect(() => {
    if (!startDate || !endDate) return
    const params = { startDate, endDate }
    dispatch(getAttendanceSummary(params))
    dispatch(getAttendanceByDepartment(params))
    dispatch(getAttendanceByRole(params))
    dispatch(getAttendanceStatusDistribution(params))
    return () => {
      dispatch(clearHrAnalytics())
    }
  }, [startDate, endDate])

  useEffect(() => {
    if (!startDate || !endDate) return
    dispatch(getAttendanceRateTrend({
      startDate,
      endDate,
      period: PERIOD_MAP[period] ?? 1,
    }))
  }, [startDate, endDate, period])

  const kpis = useMemo(
    () => [
      {
        value: (attendanceSummary?.expected ?? 0).toLocaleString(),
        label: 'EXPECTED',
        sublabel: 'Employee Days',
        accentColor: '#30B6D1',
        iconKey: 'person',
      },
      {
        value: (attendanceSummary?.present ?? 0).toLocaleString(),
        label: 'PRESENT',
        sublabel: `${toPercentLabel(attendanceSummary?.presentPercentOfExpected ?? null)} of expected`,
        accentColor: '#00B04C',
        iconKey: 'checkCircle',
      },
      {
        value: (attendanceSummary?.absent ?? 0).toLocaleString(),
        label: 'ABSENT',
        sublabel: `${toPercentLabel(attendanceSummary?.absentPercentOfExpected ?? null)} of expected`,
        accentColor: '#ff9900',
        iconKey: 'warning',
      },
      {
        value: toPercentLabel(attendanceSummary?.attendanceRate ?? null),
        label: 'ATTENDANCE RATE',
        trend: attendanceSummary?.attendanceRateChange != null
          ? {
            value: formatChange(attendanceSummary.attendanceRateChange) || '',
            positive: (attendanceSummary.attendanceRateChange ?? 0) >= 0,
          }
          : undefined,
        accentColor: '#00B04C',
        iconKey: 'chart',
      },
    ],
    [attendanceSummary],
  )

  const trendData = useMemo(
    () =>
      attendanceRateTrend.map((point) => ({
        date: formatShortDate(point.date),
        rate: toPercent(point.rate),
      })),
    [attendanceRateTrend],
  )

  const statusDistribution = useMemo(
    () =>
      attendanceStatusDistribution.map((item) => {
        const meta = item.type != null ? STATUS_META[item.type] : UNKNOWN_STATUS
        return {
          label: meta?.label ?? UNKNOWN_STATUS.label,
          percent: toPercent(item.percent),
          color: meta?.color ?? UNKNOWN_STATUS.color,
        }
      }),
    [attendanceStatusDistribution],
  )

  const deptRows = useMemo(
    () =>
      attendanceByDepartment.map((d) => ({
        name: d.departmentName,
        expected: d.expected,
        present: d.present,
        absent: d.absent,
        rate: toPercent(d.attendanceRate),
      })),
    [attendanceByDepartment],
  )

  const roleRows = useMemo(
    () =>
      attendanceByRole.map((r) => ({
        name: r.roleName,
        expected: r.expected,
        present: r.present,
        absent: r.absent,
        rate: toPercent(r.attendanceRate),
      })),
    [attendanceByRole],
  )

  const tableData = tableTab === 'department' ? deptRows : roleRows

  return (
    <>
      <KpiRow>
        {kpis.map((kpi, idx) => (
          <KpiCard
            key={idx}
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            trend={kpi.trend}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <ChartCard
        title='Attendance Rate Trend'
        periods={['Day', 'Week', 'Month']}
        activePeriod={period}
        onPeriodChange={setPeriod}
        legend={[
          { label: 'Rate', color: '#ff9900' },
          { label: '85% Target', color: '#FF4F46', dashed: true },
        ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray='3 3' stroke='#46425520' />
            <XAxis dataKey='date' tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <Tooltip />
            <ReferenceLine y={85} stroke='#FF4F46' strokeDasharray='8 8' strokeWidth={2} />
            <Line
              type='monotone'
              dataKey='rate'
              stroke='#ff9900'
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#ff9900' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCardWrapper>
        <ChartCardTitle>Status Distribution</ChartCardTitle>
        <StatusBarWrapper>
          <StatusBar>
            {statusDistribution.map((status) => (
              <StatusSegment
                key={status.label}
                percent={status.percent}
                color={status.color}
              >
                {status.percent > 5 ? `${status.percent}%` : ''}
              </StatusSegment>
            ))}
          </StatusBar>
          <StatusLegend>
            {statusDistribution.map((status) => (
              <LegendItem key={status.label}>
                <LegendDot color={status.color} />
                {status.label}
              </LegendItem>
            ))}
          </StatusLegend>
        </StatusBarWrapper>
      </ChartCardWrapper>

      <SubTabBar
        tabs={[
          { key: 'department', label: 'By Department' },
          { key: 'role', label: 'By Role' },
        ]}
        activeTab={tableTab}
        onTabChange={setTableTab}
      />

      <AnalyticsTable columns={DEPT_COLUMNS} data={tableData} />
    </>
  )
}


interface ITab {
  startDate: Date
  endDate: Date
}